import type { ILegendItems } from "app/components/React/Legends/legendsTypes";
import { chosenSites } from "app/common/services/chosenSites";

import { MAX_SELECTION } from "../../../consts";
import { formatVisits } from "../../helpers";
import type { HorizontalBarItem, LobEntry, SiteLobData } from "../types";
import { createLobEntry, createLegendItem } from "../utils";

export const processSingleModeData = (data: Record<string, SiteLobData> | null | undefined) => {
    if (!data) {
        return { lobEntries: [], availableLobs: [], lobVisitsData: {}, domainMap: {} };
    }
    const firstDomain = Object.keys(data)[0];
    const analysis = firstDomain ? data[firstDomain]?.LobAnalysis : null;

    if (!analysis || !firstDomain) {
        return { lobEntries: [], availableLobs: [], lobVisitsData: {}, domainMap: {} };
    }
    const lobEntries = Object.entries(analysis)
        .filter(([, lobData]) => lobData.Visits > 0)
        .map(([lob, lobData]) => createLobEntry(lob, lobData.Visits, lobData.Share, [firstDomain], lobData.YoYChange, lobData.lobKey))
        .sort((a, b) => b.share - a.share);

    const availableLobs = Object.keys(analysis);

    const lobVisitsData = Object.fromEntries(
        Object.entries(analysis).map(([lobName, lobData]) => [lobName, lobData.Visits || 0]),
    );

    const sites = chosenSites.sitelistForLegend();
    const domainMap = Object.fromEntries(
        sites.map((site) => [site.name, { name: site.name, url: site.name, favicon: site.icon }]),
    );

    return { lobEntries, availableLobs, lobVisitsData, domainMap };
};

export const createLegendFromEntries = (entries: LobEntry[]): ILegendItems =>
    entries.map((entry, idx) =>
        createLegendItem(entry.lob, entry.lob, entry.formattedVisits.toString(), idx, idx < MAX_SELECTION),
    );

export const createChartDataWithVisibility = (
    lobEntries: LobEntry[],
    legendItems: ILegendItems,
    data: Record<string, SiteLobData> | null | undefined,
    domainMap: Record<string, { name: string; url: string; favicon: string }>,
) => {
    if (!data) {
        return { visibleChartData: [], allChartData: [] };
    }

    const firstDomain = Object.keys(data)[0];
    const legendMap = new Map(legendItems.map((l) => [l.name, l]));

    const chartData: HorizontalBarItem[] = lobEntries.map((entry) => {
        const legend = legendMap.get(entry.lob);
        return {
            label: entry.lob,
            value: entry.formattedVisits,
            yoyChange: entry.yoyChange,
            progressBarItem: {
                percentage: entry.formattedShare,
                barFill: Math.round(entry.share * 100),
                color: legend?.color,
            },
            isTotal: entry.isTotal,
            lobKey: entry?.lobKey,
        };
    });

    const siteVisits = firstDomain ? data[firstDomain]?.SiteVisits : null;
    if (firstDomain && siteVisits != null) {
        chartData.push({
            label: firstDomain,
            value: formatVisits(siteVisits),
            isTotal: true,
            favicon: domainMap?.[firstDomain]?.favicon,
        });
    }

    const visibleNames = new Set(legendItems.filter((item) => item.visible).map((item) => item.name));
    const visibleData = chartData.filter((item) => item.isTotal || visibleNames.has(item.label));

    return { visibleChartData: visibleData, allChartData: chartData };
};
